import React from "react";
import PropTypes from "prop-types";
import { Card, Button, Icon } from "antd";
import { OrderedMap } from "immutable";
import Datasheet from "react-datasheet";
import { PermissionContextManager } from "hocs/permission-context-manager";
import { UI_LIST_ITEM_TYPES, UI_PERMISSIONS } from "constants";

const valueRenderer = (_, key, type) => {
    return _ && (type && _[key] ? _[key].toFixed(2) : _[key]);
};
const financialYearRenderer = (_, key) => (valueRenderer(_, key) || {}).name;

const changeRenderer = (f1, f2, key, type) => {
    if (f1 && f1[key] && !f2) {
        return "0.0";
    } else if (!f1 && f2 && f2[key]) {
        return "100.0";
    } else if (f1 && f2 && f1[key] && f2[key]) {
        const val = ((f1[key] - f2[key]) * 100) / f2[key];
        if (type === "fixed") {
            return val.toFixed(2);
        }
        return val;
    }
    return "--";
};

export const BALANCE_SHEET_LIABILITIES = new OrderedMap({
    financial_year: {
        label: "Year Ended",
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER,
        render: financialYearRenderer
    },
    bs_borrowings_lt1yr: {
        label: "Borrowings (<1 Year)",
        type: UI_LIST_ITEM_TYPES.SECTION,
        render: valueRenderer
    },

    bs_liabilities_lt1yr_cc_od: {
        label: "Loan From Bank (CC/OD)",
        type: UI_LIST_ITEM_TYPES.LIST_ITEM,
        render: valueRenderer
    },

    bs_liabilities_lt1yr_creditors: {
        label: "Creditors",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_lt1yr_advances: {
        label: "Advances",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_lt1yr_provision: {
        label: "Provision (Tax & Other)",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_lt1yr_tl_emi_payable: {
        label: "Term Loan Emi Payable",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_lt1yr_other_liabilities: {
        label: "Other Liabilities",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_total_borrowings_lt1yr: {
        label: "Total Borrowings",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    borrowings_gt1yr: {
        label: "Borrowings (>1 Year)",
        type: UI_LIST_ITEM_TYPES.SECTION
    },

    bs_liabilities_gte1yr_term_loan: {
        label: "Term Loan",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_gte1yr_unsecured_loan: {
        label: "Unsecured Loan",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_gte1yr_other_liabilities: {
        label: "Other Liabilities (> 1 Year)",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_total_borrowings_gte1yr: {
        label: "Total Borrowings (>1 Year)",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    bs_networth: {
        label: "Networth",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    bs_liabilities_networth_equity: {
        label: "Equity",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_networth_quasi_equity: {
        label: "Quasi Equity",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_liabilities_tnw: {
        label: "Tnw",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },

    bs_blank_name_1: { label: "", type: UI_LIST_ITEM_TYPES.SECTION },

    bs_tol: {
        label: "Tol",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },

    bs_total_debit: {
        label: "Total Debt",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },

    bs_total_liabilities: {
        label: "Total Liabilities",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    }
});

export const TRADING_PROFIT_AND_LOSS = new OrderedMap({
    financial_year: {
        label: "Year Ended",
        render: financialYearRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    revenue: {
        label: "Revenue",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    pnl_revenue_sales: {
        label: "Sales",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_revenue_other_income: {
        label: "Other Income",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_net_sales: {
        label: "Net Sales",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    purchase: {
        label: "Purchase",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    pnl_purchase_raw_material: {
        label: "Raw Material Purchase",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_purchase_spare: {
        label: "Spare Purchase",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_total_purchase: {
        label: "Total Purchase",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    cost_of_goods_sold: {
        label: "Costs of Goods Sold",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    pnl_cost_of_goods_opening_stock: {
        label: "Opening Stock",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_cost_of_goods_closing_stock: {
        label: "Closing Stock P&F",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_cost_of_direct_expense: {
        label: "Direct Expense",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_cogs: {
        label: "COGS",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    gross_profit: {
        label: "Gross Profit",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    pnl_gross_profit: {
        label: "Gross Profit",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    selling_and_admin_expense: {
        label: "Selling and Admin Expense",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    pnl_expense_salary_to_staff: {
        label: "Salary To Staff",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_expense_depreciation: {
        label: "Depreciation P&F",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_expense_other_expenses: {
        label: "Other Expenses",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_total_expense: {
        label: "Total Expense",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    finance_cost: {
        label: "Finance Cost",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    pnl_finance_cost_interest_paid: {
        label: "Interest Paid",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_ebidta: {
        label: "EBIDTA",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    pnl_pbit: {
        label: "PBIT",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    pnl_pbt: {
        label: "PBT",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    pnl_taxes: {
        label: "Taxes",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },
    pnl_pat: {
        label: "PAT",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    }
});

export const BALANCE_SHEET_ASSETS = new OrderedMap({
    financial_year: {
        label: "Year Ended",
        render: financialYearRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    bs_current_asset: {
        label: "Current Asset",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    bs_assets_ca_cash_bank_balance: {
        label: "Cash & Bank Balance",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_ca_investment: {
        label: "Investment (Current Asset)",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_ca_debtors: {
        label: "Debtors",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_ca_closing_stock: {
        label: "Closing Stock",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_ca_advance_tax: {
        label: "Advance Tax",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_ca_loan_and_advances: {
        label: "Loan And Advances",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_ca_other_current_asset: {
        label: "Other Current Asset",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_total_current_asset: {
        label: "Total Current Asset",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },

    bs_fixed_asset: {
        label: "Fixed Asset",
        type: UI_LIST_ITEM_TYPES.SECTION
    },

    bs_assets_fa_gross_block: {
        label: "Gross Block",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_fa_depreciation: {
        label: "Depreciation",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_total_fixed_asset: {
        label: "Net Block",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    bs_non_current_asset: {
        label: "Non Current Asset",
        type: UI_LIST_ITEM_TYPES.SECTION
    },

    bs_assets_nca_loans_and_advances: {
        label: "Loans And Advances",
        render: (_, key) => _ && _[key],
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_nca_debtor: {
        label: "Debtor",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_nca_deferred_taxes: {
        label: "Deferred Taxes",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_nca_investment: {
        label: "Investment (Non Current Asset)",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_assets_nca_other_nca: {
        label: "Other Non Current Asset",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM
    },

    bs_total_non_current_asset: {
        label: "Total Non Current Asset",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    bs_total_asset: {
        label: "Total Asset",
        render: valueRenderer,
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER
    },
    difference_in_bs: {
        label: "Difference in Balance Sheet",
        type: UI_LIST_ITEM_TYPES.SECTION
    },
    bs_difference: {
        label: "Difference in BS",
        type: UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER,
        render: valueRenderer
    }
});

class FinancialAnalysisForm extends React.Component {
    state = {
        data: []
    };
    edit = true;
    getCols = formName => {
        if (formName === "BalanceSheetLiabilitiesForm") {
            return BALANCE_SHEET_LIABILITIES;
        }
        if (formName === "BalanceSheetAssetsForm") {
            return BALANCE_SHEET_ASSETS;
        }
        if (formName === "TradingProfitAndLoss") {
            return TRADING_PROFIT_AND_LOSS;
        }
    };
    generateCols(f1, f2) {
        const cols = this.getCols(this.props.formName);
        return cols
            .map((_, key) => {
                if (_.type === UI_LIST_ITEM_TYPES.SECTION) {
                    return [
                        {
                            value: _.label,
                            readOnly: true,
                            type: _.type,
                            colSpan: 4
                        }
                    ];
                }
                if (_.type === UI_LIST_ITEM_TYPES.LIST_ITEM_HEADER) {
                    return [
                        {
                            value: _.label,
                            readOnly: true,
                            type: _.type,
                            colSpan: 1
                        },

                        {
                            value: _.render(f2, key, "fixed"),
                            readOnly: true,
                            type: _.type,
                            colSpan: 1
                        },
                        {
                            value: _.render(f1, key, "fixed"),
                            readOnly: true,
                            type: _.type,
                            colSpan: 1
                        },
                        {
                            value:
                                _.label === "Year Ended"
                                    ? "Change %"
                                    : changeRenderer(f1, f2, key, "fixed"),
                            readOnly: true,
                            type: _.type,
                            colSpan: 1
                        }
                    ];
                }
                return [
                    {
                        value: _.label,
                        readOnly: true,
                        type: _.type
                    },

                    {
                        value: String(_.render(f2, key, "fixed") || "0.0"),
                        type: _.type,
                        itemKey: key,
                        readOnly: !f2 || !this.props.FINANCIAL_ANALYSIS_EDIT
                    },
                    {
                        value: String(_.render(f1, key, "fixed") || "0.0"),
                        type: _.type,
                        itemKey: key,
                        readOnly: !f1 || !this.props.FINANCIAL_ANALYSIS_EDIT
                    },
                    {
                        value: changeRenderer(f1, f2, key, "fixed"),
                        type: _.type,
                        itemKey: key,
                        readOnly: true
                    }
                ];
            })
            .toList()
            .toArray();
    }
    componentDidMount() {
        this.edit = true;
        this.setState({
            data: this.generateCols(this.props.f1, this.props.f2)
        });
    }
    // shouldComponentUpdate() {}
    componentDidUpdate(prevProps) {
        if (
            (!this.edit && this.props.f1 && this.props.f2) ||
            (!prevProps.f1 && this.props.f1)
        ) {
            this.setState({
                data: this.generateCols(this.props.f1, this.props.f2)
            });
            this.edit = true;
            return;
        }
        this.edit = false;
    }
    save = () => {
        this.saveActive = false;
        const obj1 = {},
            obj2 = {};
        this.state.data
            .filter(_ => _[0].type === UI_LIST_ITEM_TYPES.LIST_ITEM)
            .map(([, v1, v2]) => {
                obj1[v1.itemKey] = v1.value === "" ? 0 : Number(v1.value);
                obj2[v2.itemKey] = v2.value === "" ? 0 : Number(v2.value);
            });
            console.log("inside save", obj1, obj2, this.state.data);
        this.props.save(this.props.f1?.merge(obj2), this.props.f2?.merge(obj1));
    };
    render() {
        return (
            <Card
                title={this.props.title}
                className={"padless-card mb-10"}
                extra={[
                    <Button
                        key="action-save"
                        disabled={this.props.recreditViewOnlyPermission}
                        type={"link"}
                        onClick={this.save}
                    >
                        Save <Icon type={"save"} />
                    </Button>
                ]}
            >
                <div className={"spreadsheet-table"}>
                    <Datasheet
                        data={this.state.data}
                        valueRenderer={cell =>
                            cell.value && cell.value._isAMomentObject
                                ? cell.value.format("MMM-YYYY")
                                :  cell.value &&  cell.value > 0 ? cell.value : isNaN(cell.value) ? cell.value : "--"
                        }
                        onCellsChanged={changes => {
                            this.edit = true;
                            this.saveActive = true;
                            const grid = this.state.data.map(row => [...row]);
                            changes.forEach(({ row, col, value }) => {
                                let error = false;
                                if (
                                    value &&
                                    value.length > 0 &&
                                    isNaN(Number(value))
                                ) {
                                    error = true;
                                    this.saveActive = false;
                                }
                                grid[row][col] = {
                                    ...grid[row][col],
                                    value,
                                    className: error ? "error-cell" : null
                                };
                            });
                            this.setState({ data: grid });
                        }}
                    />
                </div>
            </Card>
        );
    }
}

FinancialAnalysisForm.propTypes = {
    f1: PropTypes.object,
    f2: PropTypes.object,
    save: PropTypes.func,
    formName: PropTypes.string,
    FINANCIAL_ANALYSIS_EDIT: PropTypes.bool,
    title: PropTypes.string,
    recreditViewOnlyPermission: PropTypes.bool,
};

export default PermissionContextManager({
    permission: UI_PERMISSIONS.FINANCIAL_ANALYSIS_VIEW,
    propsPermissions: {
        FINANCIAL_ANALYSIS_EDIT: UI_PERMISSIONS.FINANCIAL_ANALYSIS_EDIT
    }
})(FinancialAnalysisForm);
